import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Space,
  Alert,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useConfirm } from "../../../hooks/useConfirm";
import { useMutations } from "../../../hooks/useApiMutation";

const { Text, Paragraph } = Typography;

const EVENT_OPTIONS = [
  { value: "usage.near_limit", label: "usage.near_limit" },
  { value: "usage.limit_reached", label: "usage.limit_reached" },
  { value: "payment.succeeded", label: "payment.succeeded" },
  { value: "payment.failed", label: "payment.failed" },
];

/**
 * Admin management of a subscription's webhook endpoints.
 * Backed by /v1/admin/subscriptions/{subscriptionId}/webhooks (GET, POST, PUT/{id}, DELETE/{id}).
 */
const WebhooksManager = ({ subscriptionId }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();

  const base = `/v1/admin/subscriptions/${subscriptionId}/webhooks`;

  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!subscriptionId) return;
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchWebhooks", {
        method: "get",
        endpoint: base,
      });
      setEndpoints(data?.data || []);
    } catch {
      setEndpoints([]);
    } finally {
      setLoading(false);
    }
  }, [base, subscriptionId]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = () => {
    setEditing(null);
    setNewSecret(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, events: ["usage.near_limit", "usage.limit_reached"] });
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    setNewSecret(null);
    form.setFieldsValue({
      url: record.url,
      events: (record.events || "").split(",").filter(Boolean),
      isActive: record.isActive,
      secret: undefined,
    });
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const payload = {
      url: values.url,
      events: (values.events || []).join(","),
      isActive: !!values.isActive,
      secret: values.secret || null,
    };

    if (editing) {
      await mutateRef.current("updateWebhook", {
        method: "put",
        endpoint: `${base}/${editing.webhookEndpointId}`,
        payload,
        alert: { success: "Webhook updated." },
      });
      setOpen(false);
    } else {
      const { data } = await mutateRef.current("createWebhook", {
        method: "post",
        endpoint: base,
        payload,
        alert: { success: "Webhook created." },
      });
      // The signing secret is returned only once — keep the modal open to show it.
      setNewSecret(data?.data?.secret || null);
      if (!data?.data?.secret) setOpen(false);
    }
    load();
  };

  const onDelete = (record) =>
    confirmAction({
      key: "deleteWebhook",
      method: "delete",
      endpoint: `${base}/${record.webhookEndpointId}`,
      title: "Delete webhook",
      content: `Remove ${record.url}?`,
      okText: "Delete",
      okType: "danger",
      onSuccess: load,
    });

  const columns = [
    { title: "URL", dataIndex: "url", key: "url", ellipsis: true },
    {
      title: "Events",
      dataIndex: "events",
      key: "events",
      render: (v) => (
        <Space size={4} wrap>
          {(v || "").split(",").filter(Boolean).map((e) => (
            <Tag key={e} color="geekblue">{e}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (v) => (v ? <Tag color="success">Active</Tag> : <Tag>Disabled</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button variant="ghost" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button variant="ghost" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
          Add webhook
        </Button>
        <Tooltip title="Refresh">
          <Button variant="ghost" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
      </Space>

      <Table
        rowKey="webhookEndpointId"
        columns={columns}
        dataSource={endpoints}
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal
        title={editing ? "Edit webhook" : "Add webhook"}
        open={open}
        onConfirm={newSecret ? () => setOpen(false) : handleOk}
        onClose={() => setOpen(false)}
        confirmText={newSecret ? "Done" : editing ? "Save" : "Add"}
        width={560}
      >
        {newSecret ? (
          <Alert
            type="success"
            showIcon
            message="Webhook created — copy the signing secret now"
            description={
              <div>
                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  This secret is shown only once. Use it to verify the{" "}
                  <Text code>X-Kubit-Signature</Text> header (HMAC-SHA256 of the raw body).
                </Paragraph>
                <Paragraph copyable={{ text: newSecret }} style={{ marginBottom: 0 }}>
                  <Text code>{newSecret}</Text>
                </Paragraph>
              </div>
            }
          />
        ) : (
          <Form form={form} layout="vertical">
            <Form.Item
              name="url"
              label="Endpoint URL"
              rules={[
                { required: true, message: "URL is required" },
                { type: "url", message: "Must be a valid http(s) URL" },
                { max: 500, message: "Max 500 characters" },
              ]}
            >
              <Input placeholder="https://your-app.com/webhooks/kubit" />
            </Form.Item>

            <Form.Item
              name="events"
              label="Events"
              rules={[{ required: true, message: "Select at least one event" }]}
            >
              <Select
                mode="multiple"
                options={EVENT_OPTIONS}
                placeholder="Select events to deliver"
              />
            </Form.Item>

            <Form.Item
              name="secret"
              label="Signing secret"
              tooltip="Leave blank to auto-generate. On edit, leave blank to keep the current secret."
            >
              <Input placeholder={editing ? "Leave blank to keep current" : "Leave blank to auto-generate"} />
            </Form.Item>

            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default WebhooksManager;
